import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type MappingForm = {
  resourceType: string;
  sourceResourceId: string;
  crmEventId: string;
  syncRegistration: boolean;
  syncAttendance: boolean;
  updateExisting: boolean;
  createMissing: boolean;
  /** Preserved from wizard Configure Sync — not edited on this form. */
  fieldMappings?: unknown;
  behavior?: unknown;
  schedule?: unknown;
};

export function parseMapping(raw?: string | null): MappingForm {
  try {
    const parsed = raw
      ? (JSON.parse(raw) as Partial<MappingForm> & Record<string, unknown>)
      : {};
    const sourceResourceId = String(
      parsed.sourceResourceId ?? parsed.zoomResourceId ?? "",
    );
    return {
      resourceType:
        typeof parsed.resourceType === "string" && parsed.resourceType.trim()
          ? parsed.resourceType
          : "meeting",
      sourceResourceId,
      crmEventId:
        typeof parsed.crmEventId === "string" ? parsed.crmEventId : "",
      syncRegistration: parsed.syncRegistration !== false,
      syncAttendance: parsed.syncAttendance !== false,
      updateExisting: parsed.updateExisting !== false,
      createMissing: parsed.createMissing !== false,
      fieldMappings: parsed.fieldMappings,
      behavior: parsed.behavior,
      schedule: parsed.schedule,
    };
  } catch {
    return {
      resourceType: "meeting",
      sourceResourceId: "",
      crmEventId: "",
      syncRegistration: true,
      syncAttendance: true,
      updateExisting: true,
      createMissing: true,
    };
  }
}

/** Payload for PATCH `mappingJson` (keeps legacy `zoomResourceId` alias). */
export function mappingJsonForSave(mapping: MappingForm): string {
  return JSON.stringify({
    ...mapping,
    sourceResourceId: mapping.sourceResourceId,
    zoomResourceId: mapping.sourceResourceId,
  });
}

const SYNC_OPTIONS = [
  ["syncRegistration", "Synchronize registrations"],
  ["syncAttendance", "Synchronize attendance"],
  ["updateExisting", "Update existing records"],
  ["createMissing", "Create missing records"],
] as const;

export function StreamMappingFields({
  mapping,
  scheduleMinutes,
  canManage,
  idPrefix = "mapping",
  showFieldMappingsNote = false,
  onMappingChange,
  onScheduleMinutesChange,
}: {
  mapping: MappingForm;
  scheduleMinutes: string;
  canManage: boolean;
  idPrefix?: string;
  showFieldMappingsNote?: boolean;
  onMappingChange: (next: MappingForm) => void;
  onScheduleMinutesChange: (next: string) => void;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-resourceType`}>Source Event Type</Label>
          <Input
            id={`${idPrefix}-resourceType`}
            value={mapping.resourceType}
            disabled={!canManage}
            onChange={(e) =>
              onMappingChange({
                ...mapping,
                resourceType: e.target.value,
              })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-sourceResourceId`}>
            Source Event ID
          </Label>
          <Input
            id={`${idPrefix}-sourceResourceId`}
            value={mapping.sourceResourceId}
            disabled={!canManage}
            onChange={(e) =>
              onMappingChange({ ...mapping, sourceResourceId: e.target.value })
            }
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-crmEventId`}>
            Destination Event ID (Optional)
          </Label>
          <Input
            id={`${idPrefix}-crmEventId`}
            value={mapping.crmEventId}
            disabled={!canManage}
            onChange={(e) =>
              onMappingChange({ ...mapping, crmEventId: e.target.value })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-schedule`}>
            Sync Interval (Minutes)
          </Label>
          <Input
            id={`${idPrefix}-schedule`}
            type="number"
            min={1}
            value={scheduleMinutes}
            disabled={!canManage}
            onChange={(e) => onScheduleMinutesChange(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-rs-ink">Synchronization</p>
        {SYNC_OPTIONS.map(([key, label]) => (
          <label
            key={key}
            className="flex items-center gap-2 text-sm text-rs-ink"
          >
            <input
              type="checkbox"
              checked={mapping[key]}
              disabled={!canManage}
              onChange={(e) =>
                onMappingChange({ ...mapping, [key]: e.target.checked })
              }
            />
            {label}
          </label>
        ))}
      </div>

      {showFieldMappingsNote ? (
        <details className="rounded-rs-sm border border-rs-border p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            Field mappings
          </summary>
          <p className="mt-2 text-rs-ink-muted">
            Automatic defaults: First Name, Last Name, Email, Company →
            Organization, Registration Date, Attendance Status. Custom mapping
            editors can be added per provider without changing destination
            credentials.
          </p>
        </details>
      ) : null}
    </>
  );
}
